import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/auth'; // same axios instance used everywhere else
import { Button, Input, Select, ModalShell, Pill } from '../lib/ui';

const label = { fontSize: 11, fontWeight: 700, marginBottom: 4, display: 'block', color: 'var(--muted)' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--muted)', borderBottom: '2px solid var(--border)' };
const td = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13 };
 
// ── Type metadata ─────────────────────────────────────────────────────────────
const TYPE_META = {
  percent:              { label: '% off subtotal',        color: '#1565c0', needsValue: true  },
  fixed:                { label: '£ off subtotal',        color: '#1a7a4a', needsValue: true  },
  free_delivery:        { label: 'Free delivery',         color: '#6a1b9a', needsValue: false },
  first_order_percent:  { label: '% off (first order)',   color: '#b8430a', needsValue: true  },
  first_order_fixed:    { label: '£ off (first order)',   color: '#7a5a0a', needsValue: true  },
};
 
function typeLabel(type) {
  return TYPE_META[type]?.label || type;
}
 
function formatDiscount(type, value) {
  if (type === 'free_delivery') return 'Free delivery';
  if (type === 'percent' || type === 'first_order_percent') return `${value}% off`;
  return `£${Number(value).toFixed(2)} off`;
}
 
function statusPill(doc) {
  if (!doc.enabled) return <Pill tone="bad">Disabled</Pill>;
  if (doc.expiresAt && new Date(doc.expiresAt) < new Date()) return <Pill tone="warn">Expired</Pill>;
  if (doc.maxUses != null && doc.usedCount >= doc.maxUses) return <Pill tone="warn">Exhausted</Pill>;
  return <Pill tone="ok">Active</Pill>;
}
 
// ── Blank form state ──────────────────────────────────────────────────────────
const BLANK = {
  code: '', label: '', type: 'percent', value: '10',
  maxUses: '', minOrderValue: '', expiresAt: '',
  isReferral: false, enabled: true,
};
 
// ── Create / Edit panel ───────────────────────────────────────────────────────
function CodeForm({ initial = BLANK, onSave, onCancel, isAdmin, saving }) {
  const [f, setF] = useState({ ...BLANK, ...initial });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
 
  const needsValue = TYPE_META[f.type]?.needsValue ?? true;
 
  const handleSave = () => {
    if (!f.code.trim()) return;
    onSave({
      ...f,
      code:          f.code.trim().toUpperCase(),
      value:         needsValue ? (Number(f.value) || 0) : 0,
      maxUses:       f.maxUses       !== '' ? Number(f.maxUses)       : null,
      minOrderValue: f.minOrderValue !== '' ? Number(f.minOrderValue) : null,
      expiresAt:     f.expiresAt     || null,
    });
  };
 
  return (
    <div style={{ padding: 16, background: '#fff', borderRadius: 'var(--r-2xl)', border: '1px solid var(--border)', marginTop: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
 
        {/* Code */}
        <div>
          <span style={label}>Code <span style={{ color: '#e53e3e' }}>*</span></span>
          <Input
            value={f.code}
            onChange={(e) => set('code', e.target.value.toUpperCase())}
            placeholder="SAVE10"
            style={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}
          />
        </div>
 
        {/* Label */}
        <div>
          <span style={label}>Label (admin only)</span>
          <Input value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="Summer promo" />
        </div>
 
        {/* Type */}
        <div>
          <span style={label}>Type <span style={{ color: '#e53e3e' }}>*</span></span>
          <Select value={f.type} onChange={(e) => set('type', e.target.value)}>
            {Object.entries(TYPE_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </Select>
        </div>
 
        {/* Value */}
        {needsValue && (
          <div>
            <span style={label}>{f.type.includes('percent') ? 'Percentage (%)' : 'Amount (£)'}</span>
            <Input type="number" min={0} value={f.value} onChange={(e) => set('value', e.target.value)} placeholder="10" />
          </div>
        )}
 
        {/* Max uses */}
        <div>
          <span style={label}>Max uses (blank = unlimited)</span>
          <Input type="number" min={1} value={f.maxUses} onChange={(e) => set('maxUses', e.target.value)} placeholder="e.g. 1" />
        </div>
 
        {/* Min order value */}
        <div>
          <span style={label}>Min order value £ (blank = none)</span>
          <Input type="number" min={0} step="0.01" value={f.minOrderValue} onChange={(e) => set('minOrderValue', e.target.value)} placeholder="e.g. 10.00" />
        </div>
 
        {/* Expiry */}
        <div>
          <span style={label}>Expires (blank = never)</span>
          <Input
            type="datetime-local"
            value={f.expiresAt ? f.expiresAt.slice(0, 16) : ''}
            onChange={(e) => set('expiresAt', e.target.value ? new Date(e.target.value).toISOString() : '')}
          />
        </div>
 
        {/* Flags */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-end' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.isReferral} onChange={(e) => set('isReferral', e.target.checked)} />
            Referral code
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Enabled
          </label>
        </div>
      </div>
 
      {f.isReferral && (
        <div style={{ padding: '10px 14px', background: 'var(--panel-2)', borderRadius: 'var(--r-xl)', border: '1px solid var(--border)', fontSize: 13, marginBottom: 12 }}>
          <strong>Referral code:</strong> When this code is used for the first time, both the referee (new customer)
          and the referrer will automatically receive a unique 10% reward code by email.
          The referee's email is captured at checkout; the referrer reward is flagged to you by email for forwarding.
          To fully automate referrer rewards, add a <code>referrerEmail</code> field when creating the code.
        </div>
      )}
 
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button variant="primary" onClick={handleSave} disabled={!f.code.trim() || saving}>
          {saving ? 'Saving…' : 'Save code'}
        </Button>
      </div>
    </div>
  );
}
 
// ── Redemption history drawer ──────────────────────────────────────────────────
function RedemptionDrawer({ code, redemptions, onClose }) {
  return (
    <ModalShell
      onClose={onClose} width="min(90vw, 620px)" zIndex={2000} ariaLabel={`Redemptions for ${code}`}
      backdropStyle={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'none' }}
      panelStyle={{ padding: 24, maxHeight: '80vh', overflow: 'hidden', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{code}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{redemptions.length} redemption{redemptions.length !== 1 ? 's' : ''}</div>
          </div>
          <Button onClick={onClose}>Close</Button>
        </div>

        {redemptions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)' }}>No redemptions yet.</div>
        ) : (
          <div style={{ overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--panel-2)' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Email</th>
                  <th style={th}>Order ID</th>
                  <th style={th}>Order total</th>
                </tr>
              </thead>
              <tbody>
                {[...redemptions].reverse().map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={td}>{new Date(r.usedAt).toLocaleString('en-GB')}</td>
                    <td style={td}>{r.email || '-'}</td>
                    <td style={td} title={r.orderId}>
                      <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {r.orderId ? r.orderId.slice(0, 20) + (r.orderId.length > 20 ? '…' : '') : '-'}
                      </span>
                    </td>
                    <td style={td}>{r.orderTotal != null ? `£${Number(r.orderTotal).toFixed(2)}` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </ModalShell>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────
export default function DiscountsTab({ isAdmin }) {
  const [codes,       setCodes]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [creating,    setCreating]    = useState(false);
  const [showCreate,  setShowCreate]  = useState(false);
  const [editId,      setEditId]      = useState(null);   // code string being edited
  const [drawerCode,  setDrawerCode]  = useState(null);   // code string for redemption drawer
  const [banner,      setBanner]      = useState('');
  const [q,           setQ]           = useState('');
  const [filterType,  setFilterType]  = useState('all');
  const [filterStatus,setFilterStatus]= useState('all');
  const timerRef = useRef(null);
 
  const flash = (msg) => {
    setBanner(msg);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setBanner(''), 2400);
  };
 
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/discounts');
      setCodes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      flash('Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => { load(); }, [load]);
 
  const handleCreate = async (fields) => {
    setCreating(true);
    try {
      await api.post('/discounts', fields);
      flash(`Code ${fields.code} created`);
      setShowCreate(false);
      load();
    } catch (e) {
      flash(e?.response?.data?.error || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };
 
  const handleEdit = async (fields) => {
    setCreating(true);
    try {
      await api.patch(`/discounts/${encodeURIComponent(editId)}`, fields);
      flash(`Code ${editId} updated`);
      setEditId(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.error || 'Failed to update code');
    } finally {
      setCreating(false);
    }
  };
 
  const handleDelete = async (code) => {
    if (!window.confirm(`Delete code "${code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/discounts/${encodeURIComponent(code)}`);
      flash(`Deleted ${code}`);
      load();
    } catch (e) {
      flash('Failed to delete code');
    }
  };
 
  const toggleEnabled = async (doc) => {
    try {
      await api.patch(`/discounts/${encodeURIComponent(doc.code)}`, { enabled: !doc.enabled });
      setCodes((list) => list.map((c) => c.code === doc.code ? { ...c, enabled: !c.enabled } : c));
    } catch { flash('Failed to update'); }
  };
 
  const isExpired  = (d) => d.expiresAt && new Date(d.expiresAt) < new Date();
  const isExhausted = (d) => d.maxUses != null && d.usedCount >= d.maxUses;
  const isActive   = (d) => d.enabled && !isExpired(d) && !isExhausted(d);
 
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return codes.filter((d) => {
      if (filterType !== 'all' && d.type !== filterType) return false;
      if (filterStatus === 'active'   && !isActive(d))   return false;
      if (filterStatus === 'inactive' &&  isActive(d))   return false;
      if (filterStatus === 'referral' && !d.isReferral)  return false;
      return `${d.code} ${d.label}`.toLowerCase().includes(term);
    });
  }, [codes, q, filterType, filterStatus]);
 
  const editDoc = codes.find((c) => c.code === editId);
  const drawerDoc = codes.find((c) => c.code === drawerCode);
 
  const chipStyle = (active) => ({
    padding: '5px 12px', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
    background: active ? 'var(--brand-2)' : '#fff', color: active ? '#fff' : 'var(--text)',
    fontWeight: 600, fontSize: 12, cursor: 'pointer',
  });
 
  // Stats
  const activeCount   = codes.filter(isActive).length;
  const referralCount = codes.filter((c) => c.isReferral).length;
  const totalUses     = codes.reduce((s, c) => s + (c.usedCount || 0), 0);
 
  return (
    <div>
      {/* Stat strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total codes',    value: codes.length, },
          { label: 'Active',         value: activeCount,  },
          { label: 'Referral codes', value: referralCount,},
          { label: 'Total uses',     value: totalUses,    },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'var(--panel-2)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)', padding: '10px 18px', minWidth: 100,
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-2)' }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>
 
      {banner && (
        <div style={{ padding: '10px 14px', background: '#ecfff1', borderRadius: 'var(--r-lg)', marginBottom: 12, fontSize: 13 }}>
          {banner}
        </div>
      )}
 
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Search codes…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 240 }}
        />
 
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['all', 'active', 'inactive', 'referral'].map((s) => (
            <button key={s} style={chipStyle(filterStatus === s)} onClick={() => setFilterStatus(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
 
        <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ width: 'auto' }}>
          <option value="all">All types</option>
          {Object.entries(TYPE_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </Select>
 
        <Button
          variant="primary"
          onClick={() => { setShowCreate((s) => !s); setEditId(null); }}
          disabled={!isAdmin}
          style={{ marginLeft: 'auto' }}
        >
          {showCreate ? 'Cancel' : '+ New code'}
        </Button>
      </div>
 
      {!isAdmin && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
          Admin access required to create or edit discount codes.
        </div>
      )}
 
      {/* Create form */}
      {showCreate && (
        <CodeForm
          onSave={handleCreate}
          onCancel={() => setShowCreate(false)}
          isAdmin={isAdmin}
          saving={creating}
        />
      )}
 
      {/* Table */}
      <div style={{ overflowX: 'auto', marginTop: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--panel-2)' }}>
              <th style={th}>Code</th>
              <th style={th}>Type</th>
              <th style={th}>Discount</th>
              <th style={th}>Limits</th>
              <th style={th}>Uses</th>
              <th style={th}>Status</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((doc) => (
              <React.Fragment key={doc.code}>
                <tr style={{ borderBottom: '1px solid var(--border)', background: editId === doc.code ? '#fffdf3' : '#fff' }}>

                  {/* Code + label */}
                  <td style={td}>
                    <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.5 }}>{doc.code}</div>
                    {doc.label && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{doc.label}</div>}
                    {doc.isReferral && <Pill tone="blue" style={{ marginTop: 4 }}>Referral</Pill>}
                    {doc.referralOf && (
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                        reward from {doc.referralOf}
                      </div>
                    )}
                  </td>

                  {/* Type */}
                  <td style={td}>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--r-sm)',
                      background: 'var(--panel-2)', color: TYPE_META[doc.type]?.color || 'var(--text)',
                    }}>
                      {typeLabel(doc.type)}
                    </span>
                  </td>
 
                  {/* Discount value */}
                  <td style={td}>
                    <strong>{formatDiscount(doc.type, doc.value)}</strong>
                  </td>
 
                  {/* Limits */}
                  <td style={td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {doc.maxUses != null && (
                        <span style={{ fontSize: 12 }}>🔢 Max {doc.maxUses} use{doc.maxUses !== 1 ? 's' : ''}</span>
                      )}
                      {doc.minOrderValue != null && (
                        <span style={{ fontSize: 12 }}>💰 Min £{Number(doc.minOrderValue).toFixed(2)}</span>
                      )}
                      {doc.expiresAt && (
                        <span style={{ fontSize: 12, color: isExpired(doc) ? '#b00020' : 'var(--muted)' }}>
                          📅 {new Date(doc.expiresAt).toLocaleDateString('en-GB')}
                        </span>
                      )}
                      {!doc.maxUses && !doc.minOrderValue && !doc.expiresAt && (
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>No limits</span>
                      )}
                    </div>
                  </td>

                  {/* Uses */}
                  <td style={td}>
                    <button
                      onClick={() => setDrawerCode(doc.code)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: 14, color: 'var(--brand-2)',
                        textDecoration: 'underline', padding: 0,
                      }}
                      title="View redemption history"
                    >
                      {doc.usedCount}
                      {doc.maxUses != null && (
                        <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--muted)' }}> / {doc.maxUses}</span>
                      )}
                    </button>
                  </td>
 
                  {/* Status */}
                  <td style={td}>{statusPill(doc)}</td>
 
                  {/* Actions */}
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Button
                        onClick={() => { setEditId(editId === doc.code ? null : doc.code); setShowCreate(false); }}
                        disabled={!isAdmin}
                      >
                        {editId === doc.code ? 'Close' : 'Edit'}
                      </Button>
                      <Button onClick={() => toggleEnabled(doc)} disabled={!isAdmin}>
                        {doc.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button variant="danger" onClick={() => handleDelete(doc.code)} disabled={!isAdmin}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
 
                {/* Inline edit form */}
                {editId === doc.code && (
                  <tr style={{ background: '#fffdf3' }}>
                    <td colSpan={7} style={{ padding: '0 12px 16px' }}>
                      <CodeForm
                        initial={{
                          ...doc,
                          value:         String(doc.value || ''),
                          maxUses:       doc.maxUses       != null ? String(doc.maxUses)       : '',
                          minOrderValue: doc.minOrderValue != null ? String(doc.minOrderValue) : '',
                          expiresAt:     doc.expiresAt || '',
                        }}
                        onSave={handleEdit}
                        onCancel={() => setEditId(null)}
                        isAdmin={isAdmin}
                        saving={creating}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
 
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>
            No codes yet - create one above.
          </div>
        )}
      </div>
 
      {/* Redemption history drawer */}
      {drawerDoc && (
        <RedemptionDrawer
          code={drawerDoc.code}
          redemptions={drawerDoc.redemptions || []}
          onClose={() => setDrawerCode(null)}
        />
      )}
    </div>
  );
}